import { useState } from 'react'
import ServiceCard from '../services/ServiceCard'
import GoldButton from '../../Ui/GoldButton'
import Icon, { ICONS } from '../../Ui/Icon'
import Modal from '../../Admin/common/Modal'
import {
  useGetServicesQuery,
  useDeleteServiceMutation,
  useUpdateServiceMutation,
  useCreateServiceMutation,
} from '../../store/api/services'

const CATEGORIES = ['All', 'Detailing', 'Mechanical', 'Electrical', 'Bodywork']
const EMPTY_FORM  = { name: '', description: '', price: '', duration_hours: '' }

const inputStyle = {
  width: '100%', background: 'var(--bg-input)',
  border: '1px solid var(--border-input)', borderRadius: 8,
  padding: '9px 14px', color: 'var(--text-primary)',
  fontSize: 13, fontFamily: "'Barlow',sans-serif",
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
}
const labelStyle = {
  fontSize: 10, color: 'var(--gold-label)', textTransform: 'uppercase',
  letterSpacing: '0.14em', fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 6,
}

export default function Services() {
  const [category, setCategory]         = useState('All')
  const [search, setSearch]             = useState('')
  const [formModal, setFormModal]       = useState(null)  // null | 'create' | service object
  const [deleteTarget, setDeleteTarget] = useState(null)  // null | service object
  const [form, setForm]                 = useState(EMPTY_FORM)

  const { data: services = [], isLoading, isError } = useGetServicesQuery()
  const [createService, { isLoading: creating }]    = useCreateServiceMutation()
  const [updateService, { isLoading: updating }]    = useUpdateServiceMutation()
  const [deleteService, { isLoading: deleting }]    = useDeleteServiceMutation()

  const filtered = services.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  )

  const avgPrice    = services.length
    ? Math.round(services.reduce((a, s) => a + parseFloat(s.price), 0) / services.length)
    : 0
  const mostPopular = services.length ? services[services.length - 1].name : '—'

  const openCreate = ()    => { setForm(EMPTY_FORM); setFormModal('create') }
  const openEdit   = (svc) => { setForm(svc);        setFormModal(svc)      }
  const handleInput = (e)  => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleFormSubmit = async () => {
    try {
      if (formModal === 'create') await createService(form).unwrap()
      else                        await updateService({ id: formModal.id, ...form }).unwrap()
      setFormModal(null)
    } catch {}
  }

  const handleDelete = async () => {
    try {
      await deleteService(deleteTarget.id).unwrap()
      setDeleteTarget(null)
    } catch {}
  }

  return (
    <div>

      {/* ── Form Modal (Create / Edit) ── */}
      {formModal && (
        <Modal
          title={formModal === 'create' ? 'Add Service' : 'Edit Service'}
          onClose={() => setFormModal(null)}
          onConfirm={handleFormSubmit}
          confirmText={formModal === 'create' ? 'Create' : 'Update'}
          loading={creating || updating}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Service Name',   name: 'name',           type: 'text',   placeholder: 'e.g. Oil Change'   },
              { label: 'Description',    name: 'description',    type: 'text',   placeholder: 'Brief description' },
              { label: 'Price (₹)',      name: 'price',           type: 'number', placeholder: '0.00'              },
              { label: 'Duration (hrs)', name: 'duration_hours', type: 'number', placeholder: '1.5'               },
            ].map(({ label, name, type, placeholder }) => (
              <div key={name}>
                <div style={labelStyle}>{label}</div>
                <input
                  name={name} type={type} step="0.1"
                  placeholder={placeholder} value={form[name]}
                  onChange={handleInput} required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                  onBlur={e  => e.target.style.borderColor = 'var(--border-input)'}
                />
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <Modal
          title="Delete Service"
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
          loading={deleting}
          danger
        >
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontFamily: "'Barlow',sans-serif", margin: 0 }}>
            Are you sure you want to delete&nbsp;
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{deleteTarget.name}</span>?
            <br />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, display: 'block' }}>
              This action cannot be undone.
            </span>
          </p>
        </Modal>
      )}

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">Services</h2>
          <p className="page-subtitle">{services.length} services available</p>
        </div>
        <GoldButton onClick={openCreate}>
          <Icon d={ICONS.plus} size={14} /> &nbsp;Add Service
        </GoldButton>
      </div>

      {/* Filters */}
      <div className="filter-row">
        <div className="search-box" style={{ width: 220 }}>
          <Icon d={ICONS.search} size={14} />
          <input
            placeholder="Search services..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {CATEGORIES.map(c => (
          <button
            key={c}
            className={`filter-pill ${category === c ? 'active' : ''}`}
            onClick={() => setCategory(c)}
          >{c}</button>
        ))}
      </div>

      {/* Summary */}
      <div className="summary-grid">
        {[
          { label: 'Total Services', value: services.length,                        color: 'var(--gold)' },
          { label: 'Total Bookings', value: services.length,                        color: '#4ADE80'     },
          { label: 'Avg Price',      value: `₹${avgPrice.toLocaleString('en-IN')}`, color: '#60A5FA'     },
          { label: 'Most Popular',   value: mostPopular,                            color: '#A78BFA'     },
        ].map(s => (
          <div key={s.label} className="panel" style={{ padding: '14px 18px' }}>
            <div style={{
              fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.12em',
              textTransform: 'uppercase', fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 6,
            }}>{s.label}</div>
            <div style={{
              fontSize: 20, fontFamily: "'Bebas Neue',sans-serif",
              color: s.color, letterSpacing: '0.06em',
            }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* States */}
      {isLoading && <p style={{ color: 'var(--text-muted)', marginTop: 32 }}>Loading services...</p>}
      {isError   && <p style={{ color: '#F87171',          marginTop: 32 }}>Failed to load services.</p>}

      {/* Card Grid */}
      {!isLoading && !isError && (
        <div className="card-grid">
          {filtered.map((s, i) => (
            <ServiceCard
              key={s.id}
              service={s}
              index={i}
              delay={i * 0.07}
              onEdit={openEdit}
              onDelete={(svc) => setDeleteTarget(svc)}
            />
          ))}
          {filtered.length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>No services found.</p>
          )}
        </div>
      )}

    </div>
  )
}
